/**
 * SchwabConnectionBanner — v0.5.4 AE-062 / AE-064.
 *
 * Polls `/portfolio/connection` (a thin truth-source the backend exposes
 * via `BrokerReadStatus` on `request.state`) and renders one of:
 *
 *   - warn banner when the operator has Schwab tokens but the read path
 *     is failing (token refresh, network, malformed payload). This is
 *     the "we wanted real data but it's down" case — the most important
 *     state to surface because the pages otherwise fall back silently to
 *     SimBroker.
 *   - small "paper" pill when the operator has not connected Schwab and
 *     the SIM broker is in use by design.
 *   - small "live" pill when shadow-trading is working.
 *
 * Before v0.5.4 nothing in the UI distinguished these three cases, and
 * /today / /portfolio would render synthetic $100k as if it were real.
 */
import { useEffect, useState } from 'react';
import * as api from '../lib/api';

export function SchwabConnectionBanner({ compact = false }: { compact?: boolean }) {
  const [conn, setConn] = useState<api.ConnectionStatus | null>(null);
  useEffect(() => {
    let alive = true;
    api.portfolio
      .connection()
      .then((c) => {
        if (alive) setConn(c);
      })
      .catch(() => {
        if (alive) setConn(null);
      });
    return () => {
      alive = false;
    };
  }, []);

  if (!conn) return null;

  if (conn.status === 'schwab_unavailable') {
    return (
      <div
        className="card p-3 border-warn bg-bg-elev"
        data-testid="schwab-disconnected-banner"
      >
        <div className="text-sm font-semibold text-warn">Schwab data unavailable</div>
        <div className="text-[11px] text-ink-mute mt-0.5">
          Your account is connected but the read path failed
          {conn.error ? `: ${conn.error}` : '.'}{' '}
          Numbers render as &quot;—&quot; until the connection recovers.{' '}
          <a href="/settings?tab=brokerage" className="underline">
            Reconnect in Settings
          </a>
          .
        </div>
      </div>
    );
  }

  if (compact) {
    if (conn.status === 'schwab') {
      return (
        <span
          className="pill text-ok border-[#1f6a3a]"
          title="Numbers are from your real Schwab account."
          data-testid="source-badge-live"
        >
          live
        </span>
      );
    }
    return (
      <span
        className="pill text-ink-mute"
        title="Numbers are synthetic SimBroker data. Connect Schwab in Settings for live."
        data-testid="source-badge-paper"
      >
        paper
      </span>
    );
  }

  return null;
}
